/*
 * upgrade-shims.js — bridges 1.5.4 behaviors that SS v1.8.0's
 * main.js + secondary.js don't reproduce identically.
 *
 * Each shim is documented with: what's broken, why, and how
 * the shim fixes it. Standalone — no dependencies on theme code
 * beyond standard DOM + SS's PUB_SUB_EVENTS pattern.
 *
 * Load order: AFTER main.js + secondary.js (defer ordering in
 * layout/theme.liquid).
 */

(function () {
  'use strict';

  // ─── Shim 1: variant → main media gallery active slide ─────────────────
  // Symptom: when "hide other variants' media after selecting a variant"
  // is ON, clicking a variant updates the thumbnail strip correctly but
  // the main gallery keeps showing the previous image (often the very
  // first product media, regardless of which variant the customer picked).
  //
  // Cause: the hide-variants system is purely CSS-based. Every slide that
  // belongs to ANY variant gets `.product__media-item--variant`, which the
  // theme stylesheet hides via `display:none` — except when that slide is
  // `:first-child`, in which case it's restored to `display:block`. So for
  // the right variant image to show, the matching <li> must literally be
  // the first child of the slider <ul>, AND the slider's scroll position
  // must be reset to 0.
  // SS v1.8.0's bundled setActiveMedia keeps the thumbnail aria-current in
  // sync but does NOT reliably move the main-gallery <li> to first or
  // reset the slider's horizontal scroll, so the merchant sees a stale
  // image in the main viewport.
  //
  // Fix: subscribe to PUB_SUB_EVENTS.variantChange. For each variant
  // change:
  //   1. Look up the variant's featured_media id.
  //   2. For every <media-gallery> on the page, find the <li> whose
  //      data-media-id matches `{section}-{featured_media.id}` in the
  //      MAIN slider (`slider-component.product-media-slider`) and move
  //      it to position 1 (prepend).
  //   3. Do the same for the THUMBNAIL slider — the matching <li> uses
  //      `data-target` instead of `data-media-id`.
  //   4. Reset the main slider's scrollLeft to 0 so the first-child
  //      slide (now the correct variant) lands in the viewport.
  //   5. Toggle `.is-active` so any CSS or accessibility hooks that look
  //      for the active slide pick up the new one.
  //   6. Call resetPages() on every <slider-component> in the gallery so
  //      pagination/scroll math tracks the new slide order.
  // We DO NOT call setActiveMedia from here — SS owns that path and
  // calling it again with our own arg shape causes a duplicate prepend
  // and the wrong slide ends up active.

  function getMediaIdFromEvent(event) {
    // Shape varies across SS minor versions. Try several known locations.
    var data = event && event.data;
    if (!data) return null;
    var variant = data.variant || (data.selectedVariant) || null;
    if (variant && variant.featured_media && variant.featured_media.id) {
      return variant.featured_media.id;
    }
    if (variant && variant.featured_image && variant.featured_image.id) {
      // Some variants ship `featured_image.id` matching a media.id; fall
      // back to it when featured_media is absent.
      return variant.featured_image.id;
    }
    return null;
  }

  function moveSlideToFront(listEl, slideEl) {
    if (!listEl || !slideEl || listEl.firstElementChild === slideEl) return false;
    listEl.insertBefore(slideEl, listEl.firstElementChild);
    return true;
  }

  function syncGalleryToMedia(galleryEl, mediaId) {
    if (!galleryEl || !mediaId) return;
    var section = galleryEl.dataset.section;
    if (!section) return;
    // Respect the merchant's "Disable prepend of the new active media"
    // setting (data-disable-prepend on <media-gallery>). When prepend is
    // disabled the gallery is expected to stay where the customer left it,
    // so we MUST NOT move slides or reset scroll — that would override the
    // exact behavior the merchant opted into.
    var prependDisabled = galleryEl.dataset.disablePrepend === 'true';
    if (prependDisabled) return;

    var target = section + '-' + mediaId;
    var didMoveMain = false;

    // 1. Main media slider — move active slide to position 1 so the
    // (specificity-corrected) `:first-child` CSS rule unhides it.
    var mainSlider = galleryEl.querySelector('slider-component.product-media-slider');
    if (mainSlider) {
      var mainList = mainSlider.querySelector('ul.product__media-list');
      var activeSlide = mainSlider.querySelector('[data-media-id="' + target + '"]');
      if (mainList && activeSlide) {
        moveSlideToFront(mainList, activeSlide);
        didMoveMain = true;
        // Toggle is-active so any CSS / a11y selectors keying off it follow
        mainList.querySelectorAll('.product__media-item.is-active').forEach(function (n) {
          if (n !== activeSlide) n.classList.remove('is-active');
        });
        activeSlide.classList.add('is-active');
        // Reset scroll only when we actually have a target slide to show.
        // No target = unchanged DOM = current scroll position is still valid.
        var scrollEl = mainSlider.querySelector('[id^="Slider-Gallery-"]');
        if (scrollEl) {
          try { scrollEl.scrollLeft = 0; } catch (e) {}
        }
      }
    }

    // 2. Thumbnail slider — matching <li> uses data-target. Mirror the
    // move so the thumbnail strip's own `:not(:first-child){display:none}`
    // rule unhides the matching thumbnail.
    var thumbSlider = galleryEl.querySelector('slider-component.thumbnail-slider');
    if (thumbSlider) {
      var thumbList = thumbSlider.querySelector('ul.thumbnail-list');
      var activeThumb = thumbSlider.querySelector('[data-target="' + target + '"]');
      if (thumbList && activeThumb) {
        moveSlideToFront(thumbList, activeThumb);
        thumbList.querySelectorAll('button[aria-current="true"]').forEach(function (b) {
          b.removeAttribute('aria-current');
        });
        var btn = activeThumb.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');
        var thumbScroll = thumbSlider.querySelector('[id^="Slider-Thumbnails-"]');
        if (thumbScroll) {
          try { thumbScroll.scrollLeft = 0; } catch (e) {}
        }
      }
    }

    // 3. Refresh every slider-component's cached pagination/scroll math
    // (only meaningful if we actually moved a slide).
    if (didMoveMain) {
      galleryEl.querySelectorAll('slider-component').forEach(function (slider) {
        if (typeof slider.resetPages === 'function') {
          try { slider.resetPages(); } catch (e) {}
        } else if (typeof slider.initPages === 'function') {
          try { slider.initPages(); } catch (e) {}
        }
      });
    }
  }

  // Coalesce the two variant events into a single sync per click.
  //
  // A single variant selection fires BOTH PUB_SUB_EVENTS.variantChange and
  // PUB_SUB_EVENTS.optionValueSelectionChange (see subscriptions below),
  // one frame apart. Without coalescing, syncGalleryToMedia — which
  // reorders the active slide, resets scrollLeft, and re-runs the slider's
  // pagination math (resetPages -> initPages -> update) — executes TWICE.
  // Each pass repaints the slider counter against a half-settled scroll
  // position, which is what made the counter sweep (1 -> 2 -> 3 -> 4) before
  // settling whenever a variant got selected and its image unhid.
  //
  // We keep BOTH subscriptions (their payload shapes differ across SS minor
  // versions and getMediaIdFromEvent relies on trying both), but cancel any
  // frame already queued so only the LAST event in a burst actually runs the
  // sync. Net effect: identical end state, one deterministic pass, no sweep.
  var pendingSyncFrame = null;
  var pendingSyncMediaId = null;

  function onVariantChange(event) {
    var mediaId = getMediaIdFromEvent(event);
    if (!mediaId) return;
    pendingSyncMediaId = mediaId;
    // Run on the next frame so SS's own DOM mutations (variant title,
    // price, picker state) land first and don't fight ours.
    if (pendingSyncFrame) cancelAnimationFrame(pendingSyncFrame);
    pendingSyncFrame = requestAnimationFrame(function () {
      pendingSyncFrame = null;
      var id = pendingSyncMediaId;
      document.querySelectorAll('media-gallery').forEach(function (gallery) {
        syncGalleryToMedia(gallery, id);
      });
    });
  }

  // Subscribe via SS's PUB_SUB_EVENTS pattern. variantChange fires once
  // the variant is fully resolved; optionValueSelectionChange fires on
  // every option click and re-syncing there keeps the gallery snappy.
  // NOTE: both events fire for a single selection, so onVariantChange
  // coalesces them into one rAF-deferred sync (see comment above it). Keep
  // BOTH subscriptions — they carry different payload shapes across SS minor
  // versions and getMediaIdFromEvent depends on trying both. Do not "simplify"
  // by dropping one or removing the coalescing; that reintroduces the
  // double-sync that made the slider counter sweep on variant change.
  function subscribeToVariantEvents() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      if (window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, onVariantChange);
      }
      if (window.PUB_SUB_EVENTS.optionValueSelectionChange) {
        window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, onVariantChange);
      }
      return true;
    }
    return false;
  }

  function trySubscribe(attempt) {
    if (subscribeToVariantEvents()) return;
    if (attempt < 20) { setTimeout(function () { trySubscribe(attempt + 1); }, 50); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { trySubscribe(0); });
  } else {
    trySubscribe(0);
  }
})();

/* ─── Shim 3: keep ATC label token nodes alive across variant changes ─────
 *
 * Symptom: with [location], [date] or [countdown] used in the Main ATC
 * button's Line 1, the resolved value is replaced by the raw token text
 * (or, for the countdown, disappears) the first time a variant is picked.
 *
 * Cause: SS's main.js, on every variant change, does
 *
 *     atc.querySelector('.main-atc__label__text').textContent =
 *       button.dataset.customLabel ?? variantStrings.addToCart;
 *
 * textContent, so the element's children are destroyed and replaced by the
 * raw `atc_custom_label` setting — which still contains the literal token.
 * (Line 2 and the price span used to be destroyed by this too; that is fixed
 * structurally in snippets/buy-buttons.liquid, which moved
 * .main-atc__label__text down onto the line-1 text span so the flatten only
 * ever hits line 1. Line 1's own token nodes still sit inside it, so they
 * still need this.)
 *
 * Fix: cache line 1's original child NODES once, and re-append those very
 * same nodes after each variant change. Re-appending the live nodes rather
 * than re-rendering markup is what keeps a running countdown running: its
 * interval callback holds a reference to the element and keeps writing to it
 * even while it is detached, so the timer carries on from where it was
 * instead of resetting to full on every variant click.
 *
 * Sold-out is left alone — main.js writes the sold-out string there and that
 * should win over the configured label.
 */
(function () {
  'use strict';

  var cache = new WeakMap();

  function textEl(btn) { return btn.querySelector('.main-atc__label__text'); }

  function remember(btn) {
    if (cache.has(btn)) return;
    var el = textEl(btn);
    if (el) cache.set(btn, Array.prototype.slice.call(el.childNodes));
  }

  function restore(btn) {
    var el = textEl(btn);
    var nodes = cache.get(btn);
    if (!el || !nodes || !nodes.length) return;
    if (btn.getAttribute('data-unavailable') === 'true') return;
    // Already intact? Bail so we never thrash the DOM on unrelated events.
    if (el.childNodes.length === nodes.length && el.firstChild === nodes[0]) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    for (var i = 0; i < nodes.length; i++) el.appendChild(nodes[i]);
  }

  function eachButton(fn) {
    document.querySelectorAll('button[data-atc-tokens]').forEach(fn);
  }

  function onVariantChange() {
    // Defer one frame so this lands after main.js's own handler.
    requestAnimationFrame(function () { eachButton(restore); });
  }

  function subscribeToVariantEvents() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      if (window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, onVariantChange);
      }
      if (window.PUB_SUB_EVENTS.optionValueSelectionChange) {
        window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, onVariantChange);
      }
      return true;
    }
    return false;
  }

  function trySubscribe(attempt) {
    if (subscribeToVariantEvents()) return;
    if (attempt < 20) { setTimeout(function () { trySubscribe(attempt + 1); }, 50); }
  }

  function start() {
    if (!document.querySelector('button[data-atc-tokens]')) return;
    // Cache after text-block.js has resolved [location]/[date] and mounted the
    // countdown, so the nodes we keep are the live, already-populated ones.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        eachButton(remember);
        trySubscribe(0);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function () {
    eachButton(function (btn) { cache.delete(btn); });
    start();
  });
})();
